import { formatCurrency } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { Property } from '@vsolv/packages/properties/domain';
import { takeUntil } from 'rxjs';
import { PropertyInputComponent, createPropertyControl } from '../../abstract-property-input.component';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'vs-number-input',
  templateUrl: './number-input.component.html',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: NumberInputComponent,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: NumberInputComponent,
      multi: true,
    },
  ],
})
export class NumberInputComponent extends PropertyInputComponent implements OnInit {
  @Input() override property!: Property.NumberModel;
  @Input() showErrors = true;

  form!: FormGroup;
  control: FormControl = new FormControl<number | null>(null);
  private onValidatorChange: () => void = () => undefined;
  private writingDisplayValue = false;

  private get currencyAmountInput(): boolean {
    return this.property.config.format === 'currency' && !this.property.config.allowedValues?.length;
  }

  override writeValue(value: number | null): void {
    // A model write must not echo through onChange, including writes before initialization.
    this.value = value;
    this.formControl?.setValue(value, { emitEvent: false });
    if (this.form) {
      // The mask can emit a view change synchronously while rendering a model write.
      this.writingDisplayValue = true;
      try {
        this.control.setValue(this.toDisplayValue(value), { emitEvent: false });
      } finally {
        this.writingDisplayValue = false;
      }
    }
  }

  override setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) this.formControl?.disable({ emitEvent: false });
    else this.formControl?.enable({ emitEvent: false });
    if (isDisabled) this.form?.disable({ emitEvent: false });
    else this.form?.enable({ emitEvent: false });
  }

  override validate(): ValidationErrors | null {
    if (this.disabled) return null;
    const errors = { ...super.validate(), ...this.control.errors };
    this.valid = Object.keys(errors).length === 0;
    return this.valid ? null : errors;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  formatValue(value: number | undefined): string {
    if (value === undefined) return '';
    return this.property.config.format === 'currency'
      ? formatCurrency(value / 100, 'en-US', '$', 'USD', '1.2-20')
      : String(value);
  }

  get allowedValueLabels(): string {
    return this.property.config.allowedValues?.map(value => this.formatValue(value)).join(', ') ?? '';
  }

  private toDisplayValue(value: unknown): unknown {
    if (value === null || value === undefined || value === '') return null;
    return this.currencyAmountInput ? Number(value) / 100 : value;
  }

  private toStoredValue(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const number = Number(value);
    if (!this.currencyAmountInput) return number;
    const cents = number * 100;
    const nearestCent = Math.round(cents);
    // Correct binary multiplication artifacts (e.g. 0.29 * 100), never round an off-step amount.
    return Math.abs(cents - nearestCent) <= 8 * Number.EPSILON * Math.max(1, Math.abs(cents)) ? nearestCent : cents;
  }

  override ngOnInit() {
    super.ngOnInit();
    const { min, max, step } = this.property.config;
    const displayProperty: Property.NumberModel = this.currencyAmountInput
      ? {
          ...this.property,
          config: {
            ...this.property.config,
            min: min == null ? undefined : min / 100,
            max: max == null ? undefined : max / 100,
            step: step == null ? undefined : step / 100,
          },
        }
      : this.property;
    this.control = createPropertyControl(displayProperty, this.required);
    this.form = new FormGroup({ 0: this.control });
    this.control.setValue(this.toDisplayValue(this.value), { emitEvent: false });

    this.control.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(value => {
      if (this.writingDisplayValue) return;
      const storedValue = this.toStoredValue(value);
      this.value = storedValue;
      this.formControl.setValue(storedValue);
    });
    this.control.statusChanges.pipe(takeUntil(this.onDestroy$)).subscribe(() => this.onValidatorChange());

    if (this.disabled) {
      this.form.disable({ emitEvent: false });
    }
  }
}
